import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { DownloadDrawingsButton } from "@ui/components/PrestigeDimensionControls";
import styles from "../ConfiguratorPage.module.css";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DesktopSummaryMetrics = {
  structureColorName: string;
  structureColorHex: string;
  louvreColorName: string;
  louvreColorHex: string;
  externalDimensions: string;
  internalDimensions: string;
  roomCount: number;
  louverCount: number;
};

export type AccessorySummaryItem = {
  partType: number;
  name: string;
  count: number;
};

export type SummaryPricing = {
  helperText?: string | null;
  /** Discounted price to charge (matches Shopify's automatic discount). */
  priceLabel: string;
  /** Full pre-discount price, shown struck through. Absent when not priced. */
  originalPriceLabel?: string | null;
  /** Discount badge text, e.g. "20% off". Absent when not priced. */
  discountLabel?: string | null;
};

type SummaryModalProps = {
  open: boolean;
  onClose: () => void;
  buildNumber: string | undefined;
  metrics: DesktopSummaryMetrics;
  accessories: AccessorySummaryItem[];
  pricing: SummaryPricing;
  pergolaDesign: string;
  louverLedKit: boolean;
  getScene: () => THREE.Scene | null;
  /**
   * Triggered by the single "Proceed to Checkout" button. Should build the
   * Shopify order/bundle product and redirect to checkout — i.e. it
   * performs the work the previous "Add to Cart" button did.
   */
  onCheckout: () => Promise<void>;
  /** Generates the engineered drawing PDF; omitted when the build is unsaved. */
  onDownloadDrawings?: () => Promise<void>;
};

// ─── Mini 3D Canvas ───────────────────────────────────────────────────────────

const MiniThreeCanvas = ({
  scene,
  onReady,
}: {
  scene: THREE.Scene | null;
  onReady?: () => void;
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scene || !canvasRef.current) return;

    const container = canvasRef.current;
    const width = container.clientWidth || 420;
    const height = container.clientHeight || 320;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.5, 500);
    camera.position.set(4.5, 2.5, 7.5);
    camera.lookAt(0, 0.5, 0);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 4;
    controls.maxDistance = 16;
    controls.target.set(0, 0.5, 0);
    controls.update();

    let animId: number;
    let firstFrame = true;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
      if (firstFrame) {
        firstFrame = false;
        onReady?.();
      }
    };
    animate();

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const observer = new ResizeObserver(handleResize);
    observer.observe(container);

    return () => {
      cancelAnimationFrame(animId);
      observer.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [scene]);

  return (
    <div
      ref={canvasRef}
      className={styles.summaryMiniCanvas}
      title="Drag to rotate"
    />
  );
};

// ─── Detail Row ───────────────────────────────────────────────────────────────

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className={styles.summaryDetailRow}>
    <span className={styles.summaryDetailLabel}>{label}</span>
    <span className={styles.summaryDetailValue}>{children}</span>
  </div>
);

// ─── SummaryModal ─────────────────────────────────────────────────────────────

export const SummaryModal = ({
  open,
  onClose,
  buildNumber,
  metrics,
  accessories,
  pricing,
  pergolaDesign,
  louverLedKit,
  getScene,
  onCheckout,
  onDownloadDrawings,
}: SummaryModalProps) => {
  const [approved, setApproved] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [scene, setScene] = useState<THREE.Scene | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);

  // Capture the scene when the modal opens
  useEffect(() => {
    if (open) {
      setScene(getScene());
      setApproved(false);
      setActionError(null);
      setCanvasReady(false);
    } else {
      setScene(null);
      setCanvasReady(false);
    }
  }, [open, getScene]);

  const handleCheckout = useCallback(async () => {
    if (!approved || checkoutLoading) return;
    setCheckoutLoading(true);
    setActionError(null);
    try {
      await onCheckout();
    } catch {
      setActionError("Failed to proceed to checkout. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  }, [approved, checkoutLoading, onCheckout]);

  if (!open) return null;

  const isLoading = checkoutLoading;

  return (
    <div
      className={styles.summaryModalBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.summaryModal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Configuration summary"
      >
        {/* ── Header ── */}
        <div className={styles.summaryModalHeader}>
          <h2 className={styles.summaryModalTitle}>
            Your pergola is looking <span>great!</span>
          </h2>
          <button
            type="button"
            className={styles.summaryModalClose}
            onClick={onClose}
            aria-label="Close summary"
          >
            ×
          </button>
        </div>

        {/* ── Body ── */}
        <div className={styles.summaryModalBody}>
          {/* Left: mini 3D canvas */}
          <div className={styles.summaryCanvasPanel}>
            <MiniThreeCanvas
              scene={scene}
              onReady={() => setCanvasReady(true)}
            />
            {!canvasReady && (
              <div className={styles.summaryCanvasLoader}>
                <div className={styles.summaryCanvasSpinner} />
                <span>Loading preview…</span>
              </div>
            )}
            {canvasReady && (
              <div className={styles.summaryCanvasHint}>Drag to rotate</div>
            )}
          </div>

          {/* Right: detail card */}
          <div className={styles.summaryDetailCard}>
            {buildNumber && (
              <DetailRow label="Build Number:">
                <strong>{buildNumber}</strong>
              </DetailRow>
            )}

            <DetailRow label="Structure Finish:">
              <span
                className={styles.summaryColorPill}
                style={{ backgroundColor: metrics.structureColorHex }}
              />
            </DetailRow>

            <DetailRow label="Louver Finish:">
              {metrics.louvreColorHex !== "#888888" ? (
                <span
                  className={styles.summaryColorPill}
                  style={{ backgroundColor: metrics.louvreColorHex }}
                />
              ) : (
                <span>-</span>
              )}
            </DetailRow>

            <DetailRow label="External Dimensions:">
              <strong>{metrics.externalDimensions}</strong>
            </DetailRow>

            <DetailRow label="Internal Dimensions:">
              <strong>{metrics.internalDimensions}</strong>
            </DetailRow>

            <DetailRow label="Lighting:">
              <strong>
                {louverLedKit ? "Louver LED Lighting Kit" : "Included LED Lighting Kit"}
              </strong>
            </DetailRow>

            <DetailRow label="Expected Shipping:">
              <strong>10-12 Weeks</strong>
            </DetailRow>

            {onDownloadDrawings && (
              <div className={styles.summaryDrawingsAction}>
                <DownloadDrawingsButton onDownload={onDownloadDrawings} />
              </div>
            )}

            {accessories.length > 0 && (
              <div className={styles.summaryAccessoriesSection}>
                <div className={styles.summaryAccessoriesHeading}>Accessories</div>
                <ul className={styles.summaryAccessoriesList}>
                  {accessories.map((acc) => (
                    <li key={acc.partType}>
                      <span>{acc.name}</span>
                      <strong>×{acc.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* ── Approval checkbox ── */}
        <div className={styles.summaryApprovalRow}>
          <label className={styles.summaryApprovalLabel}>
            <input
              type="checkbox"
              className={styles.summaryApprovalCheckbox}
              checked={approved}
              onChange={(e) => setApproved(e.target.checked)}
            />
            <span>
              By clicking here, I approve the custom product dimensions as well as the{" "}
              <a
                href="https://paragon-outdoor.com/policies/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.summaryApprovalLink}
              >
                terms and conditions policy
              </a>
              .
            </span>
          </label>
        </div>

        {/* ── Footer ── */}
        <div className={styles.summaryModalFooter}>
          <div className={styles.summaryFooterPriceBlock}>
            {pricing.originalPriceLabel && (
              <div className={styles.summaryFooterDiscountRow}>
                {pricing.discountLabel && (
                  <span className={styles.discountBadge}>{pricing.discountLabel}</span>
                )}
                <span className={styles.priceStrikethrough}>
                  {pricing.originalPriceLabel}
                </span>
              </div>
            )}
            <div className={styles.summaryFooterPrice}>{pricing.priceLabel}</div>
            <div className={styles.summaryFooterDesign}>{pergolaDesign}</div>
            <div className={styles.summaryFooterHelper}>
              Payment plans available at checkout
            </div>
          </div>

          <div className={styles.summaryFooterActions}>
            {actionError && (
              <div className={styles.summaryActionError}>{actionError}</div>
            )}
            <div className={styles.summaryFooterButtons}>
              <button
                type="button"
                className={styles.summaryCheckoutButton}
                onClick={handleCheckout}
                disabled={!approved || isLoading}
              >
                {checkoutLoading ? "Loading…" : "Proceed to Checkout"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
